import type { TFunction } from 'i18next';
import { useState } from 'react';

import { MFA_FACTOR, SMS_MFA_FEATURE_FLAG } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { useFeatureFlag } from '@repo/ui/hooks/use-feature-flag';

import emailSetupIntro from './assets/email-setup-intro.svg';
import {
  ManageActionRow,
  MfaManageDetailRow,
  MfaManageLayout,
  MfaPrimaryChoiceList,
  MfaRemoveConfirmDialog,
  type NewPrimaryMethod,
} from './mfa-authenticator-manage';
import { figmaReplacementChoices, formatMfaFactorDate } from './mfa-authenticator-manage-utils';
import { MfaHero } from './mfa-hero';
import { resolvePrimaryFactor } from './mfa-method-picker-utils';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

function remainingEmailPrimaryChoices(enrolled: readonly string[]): NewPrimaryMethod[] {
  const choices: NewPrimaryMethod[] = [];
  if (enrolled.includes(MFA_FACTOR.Totp)) {
    choices.push('totp');
  }
  if (enrolled.includes(MFA_FACTOR.Sms)) {
    choices.push('sms');
  }
  if (enrolled.includes(MFA_FACTOR.Passkey)) {
    choices.push('passkey');
  }
  return choices;
}

export function MfaEmailManageDialog({
  accountPrimary,
  email,
  enrolled,
  enrolledAt,
  isMobile,
  lastUsedAt,
  onChangeEmail: _onChangeEmail,
  onClose,
  onRemove,
  t,
}: {
  accountPrimary?: string;
  email: string;
  enrolled: readonly string[];
  enrolledAt?: string | null;
  isMobile: boolean;
  lastUsedAt?: string | null;
  onChangeEmail: () => void;
  onClose: () => void;
  onRemove: () => void;
  t: TFunction<'common'>;
}) {
  const addedOn = formatMfaFactorDate(enrolledAt);
  const lastUsedOn = formatMfaFactorDate(lastUsedAt);
  const isPrimary = resolvePrimaryFactor(enrolled, accountPrimary) === MFA_FACTOR.Email;
  const title = t('security.mfa.manageEmailTitle');
  const details = (
    <SDSFlexbox
      className={isMobile ? 'MfaAuthenticatorManage MfaAuthenticatorManage--mobile' : 'MfaAuthenticatorManage'}
      direction="column"
      fullWidth
    >
      {email ? <MfaManageDetailRow label={t('security.mfa.emailAddressField')} value={email} /> : null}
      <MfaManageDetailRow
        badge={
          <span className="MfaSettingsSection__pill MfaSettingsSection__pill--on">
            <SDSText
              color="primary"
              text={t(isPrimary ? 'security.mfa.rolePrimary' : 'security.mfa.roleBackup')}
              type="body-small"
            />
          </span>
        }
        label={t('security.mfa.methodStatus')}
        value={t(isPrimary ? 'security.mfa.yes' : 'security.mfa.no')}
      />
      {addedOn ? (
        <MfaManageDetailRow dataTestId={MFA.EMAIL_ADDED} label={t('security.mfa.added')} value={addedOn} />
      ) : null}
      {lastUsedOn ? (
        <MfaManageDetailRow dataTestId={MFA.EMAIL_LAST_USED} label={t('security.mfa.lastUsed')} value={lastUsedOn} />
      ) : null}
    </SDSFlexbox>
  );
  const actions = (
    <div className="MfaAuthenticatorManage__actions">
      <ManageActionRow
        danger
        description={isPrimary ? t('security.mfa.removeTotpChoosePrimaryFirst') : undefined}
        onClick={onRemove}
        testId={MFA.EMAIL_REMOVE}
        title={t('security.mfa.removeEmail')}
      />
    </div>
  );

  return (
    <MfaManageLayout
      actions={actions}
      className="MfaTotpDialog MfaAuthenticatorManageDialog MfaSmsManageDialog"
      closeTestId={MFA.EMAIL_CLOSE}
      dataTestId={MFA.EMAIL_MANAGE}
      hero={<MfaHero plain={isMobile} src={emailSetupIntro} />}
      isMobile={isMobile}
      onClose={onClose}
      t={t}
      title={title}
    >
      {details}
    </MfaManageLayout>
  );
}

export function MfaEmailRemoveDialog({
  accountPrimary,
  enrolled,
  isMobile,
  onBack,
  onClose,
  onConfirm,
  smsPhoneMasked,
  t,
}: {
  accountPrimary?: string;
  enrolled: readonly string[];
  isMobile: boolean;
  onBack: () => void;
  onClose: () => void;
  onConfirm: (replacement?: NewPrimaryMethod) => void;
  smsPhoneMasked?: string | null;
  t: TFunction<'common'>;
}) {
  const smsSetupAvailable = useFeatureFlag(SMS_MFA_FEATURE_FLAG);
  const remaining = remainingEmailPrimaryChoices(enrolled);
  const isPrimary = resolvePrimaryFactor(enrolled, accountPrimary) === MFA_FACTOR.Email;
  const needsPrimary = isPrimary;
  const [selected, setSelected] = useState<NewPrimaryMethod | undefined>();
  const canRemove = needsPrimary ? Boolean(selected) : true;
  const confirmRemove = () => {
    if (needsPrimary) {
      if (!selected) {
        return;
      }
      onConfirm(selected);
      return;
    }
    onConfirm();
  };
  const primaryChoices = figmaReplacementChoices('email', t, {
    passkeyCount: enrolled.includes(MFA_FACTOR.Passkey) ? 1 : 0,
    smsOffered: enrolled.includes(MFA_FACTOR.Sms) || smsSetupAvailable,
    smsPhoneMasked: enrolled.includes(MFA_FACTOR.Sms) ? (smsPhoneMasked ?? undefined) : undefined,
    totpEnrolled: remaining.includes('totp'),
  });
  const primaryField = needsPrimary ? (
    <MfaPrimaryChoiceList
      name="mfa-email-new-primary"
      onSelect={setSelected}
      options={primaryChoices}
      selected={selected}
    />
  ) : null;

  return (
    <MfaRemoveConfirmDialog
      backLabel={t('security.mfa.backToSecurity')}
      cancelLabel={t('security.mfa.cancel')}
      confirmDisabled={!canRemove}
      confirmLabel={needsPrimary ? t('security.mfa.setPrimaryAndRemove') : t('security.mfa.removeEmail')}
      dataTestId={MFA.EMAIL_REMOVE_CONFIRM}
      dialogClassName="MfaTotpDialog MfaRemoveConfirmDialog"
      hero={<MfaHero plain={isMobile} src={emailSetupIntro} />}
      isMobile={isMobile}
      lead={needsPrimary ? t('security.mfa.removeEmailChoosePrimaryLead') : t('security.mfa.removeEmailDescription')}
      onBack={onBack}
      onClose={onClose}
      onConfirm={confirmRemove}
      title={t('security.mfa.removeEmailTitle')}
    >
      {primaryField}
    </MfaRemoveConfirmDialog>
  );
}
